/**
 * THE ROOT OF THE PALETTE (2026-09-01).
 *
 * Every brand colour on the platform starts here — one constant, one place.
 * `lightTheme` below derives from it, `createCssVariables` emits it as
 * `--brand-*` custom properties, and the /desktop embed's CDN token sheet is
 * generated from it (scripts/generate-desktop-embed-tokens.mjs).
 *
 * WHY IT MOVED HERE. Until this change the palette was a source of truth in
 * name only: 102 live files painted 1,237 hardcoded brand hex literals, and
 * exactly 14 read the shared variables. Editing this file changed almost
 * nothing. The literals are now `var(--brand-*)` references, so changing a
 * value here changes it everywhere — which is what a source of truth means.
 *
 * `--brand-*` values NEVER invert with a colour scheme. That is the point of
 * having them alongside the semantic `--color-*` tokens: a landing-page hero
 * that is deliberately near-black must stay near-black, while
 * `--color-text-primary` is free to flip when a dark theme is switched on.
 * Use a brand token for "this specific brand colour", a semantic token for
 * "the role this colour plays".
 *
 * Adding a colour: add it here, and `scripts/check-design-tokens.mjs` will
 * keep the literals from creeping back.
 *
 * THE SEMANTIC LAYER HAS ITS OWN SSOT (design-system PR-1, 2026-09-23).
 * `--color-<role>` is emitted from config/design-tokens.json through its
 * generated twin ./semanticTokens.generated.ts — every role in BOTH themes,
 * with the sentence that says when it is used. This file emits the light
 * values in `:root` and the dark values in `:root[data-theme="dark"]`, which
 * only src/context/AppContext.tsx (and the pre-paint script in index.html)
 * ever sets. Today's `--color-*` names are kept, byte-identical in light —
 * see LEGACY_COLOR_ROLES.
 */
import {
  SEMANTIC_TOKEN_NAMES,
  semanticDark,
  semanticLight,
  type SemanticTokenName,
  type SemanticTokens,
} from './semanticTokens.generated';

export const brandColors = {
  primaryBlack: '#1A1A1A',
  darkVariant: '#111111',
  secondaryDark: '#2B2B2B',
  /**
   * GOLD IS A FILL, NEVER TEXT ON A LIGHT GROUND (2026-09-12).
   *
   * accentGold, accentGoldAlt and hoverGold are all surface colours. As text
   * on the house's light grounds they measure 2.0–2.9:1 against a 4.5
   * requirement, and 33 of the 57 findings in the first WCAG audit were one
   * of the three used in a `color:` role — links, badges, eyebrows, an icon
   * glyph, and the chat strip's thread chip, which renders on every page.
   *
   *   · gold text on a light ground  → accentText (--brand-gold-text)
   *   · text ON a gold fill          → secondaryDark (--brand-charcoal), 6.1:1
   *                                    (white on gold is 2.31:1)
   *   · gold on a DARK ground        → accentGold is correct and readable
   *
   * That last line is why this is prose and not a lint rule: the same token
   * is right or wrong depending on what is behind it, which no static check
   * can see. The a11y gate enforces it on every audited page.
   */
  accentGold: '#C6A75E',
  // NOTE: one hex digit from accentGold. Both are live in the product today,
  // so both are kept as distinct tokens rather than silently unified — that
  // is a visual decision for the owner, flagged 2026-09-01, not a refactor.
  // D-2 (2026-09-23) resolves it BY USE in the semantic layer: --color-accent
  // is #C6A75E by day and #C8A75E at dusk. The brand constant stays until PR-5.
  accentGoldAlt: '#C8A75E',
  hoverGold: '#B6924C',
  accentText: '#7a6125',
  backgroundSoftWhite: '#FAFAF8',
  surfaceSoft: '#F5F5F3',
  textPrimary: '#111111',
  textSecondary: '#4A4A4A',
  /**
   * The ONE muted grey (2026-09-12, WCAG 2.1 AA burn-down).
   *
   * The house had two tiers of warm muted text — var(--brand-text-muted) and the fainter
   * var(--brand-text-muted) — and at AA on a near-white ground the distinction is not
   * available: 4.5:1 against #FBFAF7 requires #787268 or darker, which is
   * already darker than the DARKER of the two. So they collapse into one.
   * The hue is unchanged and the shift is about 10% of lightness; what is
   * lost is a tier that was never readable at 12px, which is the size both
   * were actually used at.
   *
   * Text only. Neither predecessor was ever a background, a border or a
   * fill — checked across all 87 usages before the change, because a
   * darkened background is a visual regression where darkened text is not.
   *
   * The value clears 4.5:1 on EVERY ground the house uses, not just the one
   * the audit happened to catch it on. Solved against #F1EBDC, the darkest
   * cream in the palette (4.59:1); the first attempt was picked against
   * #FBFAF7 alone and measured 4.01 there and 4.37 on --brand-surface — it
   * would have passed the audit and failed the day muted text landed on a
   * cream card, which is the failure this whole burn-down is about.
   */
  textMuted: '#6E695F',
  /**
   * 4.52:1 on #FAFAF8. The old #ADB5BD measured 1.99:1 — placeholder text
   * carries real instructions ("tap a slice, or use the arrow keys") and is
   * held to the same 4.5 as any other small text. Kept cool, like its
   * predecessor, rather than warmed into textMuted's family.
   */
  textPlaceholder: '#6F7479',
  border: '#E6E6E6',
  borderStrong: '#D0D0D0',
  // The ONE alert red (2026-09-08, per-thread notification badges): the
  // unread badge on every thread surface, the account bell counter, the
  // hamburger counter and the routine error dot all painted their own red
  // literal before this token existed.
  dangerRed: '#E24A4A',
} as const;

export interface ThemeColors {
  primary: string;
  secondary: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  light: string;
  dark: string;
  accent: string;
  accentHover: string;

  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textPlaceholder: string;

  background: string;
  backgroundSecondary: string;
  backgroundHover: string;
  backgroundActive: string;

  borderLight: string;
  borderMedium: string;
  borderDark: string;
  borderFocus: string;

  accentSubtle: string;
  accentText: string;
}

export interface ThemeSpacing {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  xxl: string;
}

export interface ThemeFontSizes {
  tiny: string;
  small: string;
  body: string;
  subtitle: string;
  title: string;
  heading: string;
  display: string;
}

export interface ThemeRadii {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  round: string;
  pill: string;
}

export interface ThemeShadows {
  sm: string;
  md: string;
  lg: string;
  xl: string;
  focus: string;
}

export interface ThemeTransitions {
  fast: string;
  default: string;
  slow: string;
}

export interface ThemeFonts {
  display: string;
  heading: string;
  text: string;
  mono: string;
}

export interface Theme {
  colors: ThemeColors;
  spacing: ThemeSpacing;
  fontSizes: ThemeFontSizes;
  radii: ThemeRadii;
  shadows: ThemeShadows;
  transitions: ThemeTransitions;
  fonts: ThemeFonts;
}

export const lightTheme: Theme = {
  colors: {
    primary: brandColors.accentGold,
    secondary: brandColors.textSecondary,
    success: '#28a745',
    warning: '#ffc107',
    danger: '#f44336',
    info: '#17a2b8',
    light: brandColors.backgroundSoftWhite,
    dark: brandColors.primaryBlack,
    // D-2 (Miles, 2026-09-23, "go with the recommendations"): --color-accent is
    // unified on the platform gold (accentGold). It carried accentGoldAlt — one
    // hex digit away, green identical, red off by 2/255, invisible — since the
    // file existed; three sites read it. The brand constant --brand-gold-alt
    // stays for its own consumers until PR-5 retires it.
    accent: brandColors.accentGold,
    accentHover: brandColors.hoverGold,

    textPrimary: brandColors.textPrimary,
    textSecondary: brandColors.textSecondary,
    textTertiary: brandColors.textSecondary,
    textPlaceholder: brandColors.textPlaceholder,

    background: brandColors.backgroundSoftWhite,
    backgroundSecondary: brandColors.surfaceSoft,
    backgroundHover: brandColors.surfaceSoft,
    backgroundActive: '#e9ecef',

    borderLight: brandColors.border,
    borderMedium: brandColors.border,
    borderDark: brandColors.borderStrong,
    borderFocus: brandColors.accentGold,

    accentSubtle: 'rgba(198, 167, 94, 0.1)',
    accentText: brandColors.accentText
  },

  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem'
  },

  fontSizes: {
    tiny: '0.75rem',
    small: '0.875rem',
    body: '1rem',
    subtitle: '1.25rem',
    title: '1.5rem',
    heading: '2rem',
    display: '3rem'
  },

  radii: {
    xs: '2px',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    round: '50%',
    pill: '9999px'
  },

  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.05)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.05)',
    focus: '0 0 0 0.2rem rgba(198, 167, 94, 0.25)'
  },

  transitions: {
    fast: '0.15s ease',
    default: '0.3s ease',
    slow: '0.5s ease'
  },

  fonts: {
    display: "'Space Grotesk', -apple-system, BlinkMacSystemFont, sans-serif",
    heading: "'Satoshi', -apple-system, BlinkMacSystemFont, sans-serif",
    text: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    mono: "'SF Mono', SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace"
  }
};

// `darkTheme` (an unread object nothing imported, since the file existed) is
// retired: the dark values are the SSOT's, emitted below under
// :root[data-theme="dark"]. Verified with a tree-wide grep on 2026-09-23.

/**
 * THE TWO ROLLOUT SWITCHES (design-system PR-1, 2026-09-23).
 *
 * THEME_DARK_ENABLED — false removes the dark block from the emitted CSS (and
 * AppContext then resolves every preference to light), which is the one-line
 * rollback of the whole theme mechanism. The `data-theme` attribute may still
 * land on <html>; with no block reading it, it is inert.
 *
 * THEME_OS_FOLLOW — true additionally emits the dark values under
 * `@media (prefers-color-scheme: dark)`, guarded by `:root:not([data-theme="light"])`,
 * so a device set to dark gets dark before any script runs and an explicit
 * light choice still wins. It stays false until PR-6 (dark general
 * availability), because 623 hardcoded hexes would render half-dark today (C8).
 */
export const THEME_DARK_ENABLED = true;
export const THEME_OS_FOLLOW = false;

export interface ThemeCssFlags {
  darkEnabled: boolean;
  osFollow: boolean;
}

/**
 * Today's `--color-*` names, kept so nothing breaks. `key` is the lightTheme
 * field each name has always carried; `role` is the semantic role it means.
 *
 * In LIGHT the emitted value is today's lightTheme value BYTE FOR BYTE: where
 * that already equals the role's light value the name becomes a true alias
 * (`var(--color-<role>)`), and where it does not the literal is kept — so this
 * change is invisible to every consumer, and the name converges on its role in
 * PR-5 (retirements), one visible surface at a time. In DARK every name takes
 * its role's dark value, so a surface that reads a legacy name never paints a
 * light-only literal on a dark ground.
 *
 * Two mappings are decisions, recorded: `info` → ink-2 (C6: info is ink plus a
 * gold hairline, never blue), and `dark` → ink (the darkest thing by day is
 * the lightest at dusk).
 *
 * A name that IS a role name (accent, accent-hover, accent-subtle, accent-text)
 * is OWNED BY THE ROLE and emitted once, by the semantic block — emitting it
 * again as `var(--color-accent)` would be a self-reference, which CSS treats as
 * guaranteed-invalid, so the property would vanish at dusk. Found by the
 * mechanism test on the first run, 2026-09-23. lightTheme carries the role's
 * value for each of those four (D-2 for accent), so light stays byte-identical.
 */
export const LEGACY_COLOR_ROLES: ReadonlyArray<{ name: string; key: keyof ThemeColors; role: SemanticTokenName }> = [
  { name: 'primary', key: 'primary', role: 'accent' },
  { name: 'secondary', key: 'secondary', role: 'ink-2' },
  { name: 'success', key: 'success', role: 'success-ink' },
  { name: 'warning', key: 'warning', role: 'warning-ink' },
  { name: 'danger', key: 'danger', role: 'danger-ink' },
  { name: 'info', key: 'info', role: 'ink-2' },
  { name: 'light', key: 'light', role: 'canvas' },
  { name: 'dark', key: 'dark', role: 'ink' },
  { name: 'accent', key: 'accent', role: 'accent' },
  { name: 'accent-hover', key: 'accentHover', role: 'accent-hover' },
  { name: 'text-primary', key: 'textPrimary', role: 'ink' },
  { name: 'text-secondary', key: 'textSecondary', role: 'ink-2' },
  { name: 'text-tertiary', key: 'textTertiary', role: 'ink-2' },
  { name: 'text-placeholder', key: 'textPlaceholder', role: 'placeholder' },
  { name: 'background-primary', key: 'background', role: 'canvas' },
  { name: 'background-secondary', key: 'backgroundSecondary', role: 'stone' },
  { name: 'background-hover', key: 'backgroundHover', role: 'accent-subtle' },
  { name: 'background-active', key: 'backgroundActive', role: 'slab-hi' },
  { name: 'border-light', key: 'borderLight', role: 'line' },
  { name: 'border-medium', key: 'borderMedium', role: 'line' },
  { name: 'border-dark', key: 'borderDark', role: 'line-strong' },
  { name: 'border-focus', key: 'borderFocus', role: 'focus-ring' },
  { name: 'accent-subtle', key: 'accentSubtle', role: 'accent-subtle' },
  { name: 'accent-text', key: 'accentText', role: 'accent-text' },
];

/** Two spellings of one colour compare equal (#7A6125 / #7a6125, rgba with or without spaces or a trailing zero). */
const colourKey = (value: string): string => {
  const t = value.trim().toLowerCase();
  if (t.startsWith('#')) return t.length === 4 ? '#' + t.slice(1).split('').map((c) => c + c).join('') : t;
  const nums = t.match(/[\d.]+/g);
  return nums ? 'rgb(' + nums.map(Number).join(',') + ')' : t;
};

const semanticBlock = (tokens: SemanticTokens, indent: string): string =>
  SEMANTIC_TOKEN_NAMES.map((n) => `${indent}--color-${n}: ${tokens[n]};`).join('\n');

/** A legacy name the semantic block already declares: emitted once, by the role. */
const ownedByRole = ({ name, role }: { name: string; role: SemanticTokenName }): boolean => name === role;

const legacyLightBlock = (theme: Theme, indent: string): string =>
  LEGACY_COLOR_ROLES.filter((entry) => !ownedByRole(entry)).map(({ name, key, role }) => {
    const today = theme.colors[key];
    const value = colourKey(today) === colourKey(semanticLight[role]) ? `var(--color-${role})` : today;
    return `${indent}--color-${name}: ${value};`;
  }).join('\n');

const legacyDarkBlock = (indent: string): string =>
  LEGACY_COLOR_ROLES.filter((entry) => !ownedByRole(entry))
    .map(({ name, role }) => `${indent}--color-${name}: var(--color-${role});`).join('\n');

/** The dark ("dusk") declarations, shared by the attribute block and the OS-follow block. */
const darkDeclarations = (indent: string): string =>
  `${indent}color-scheme: dark;\n` +
  `${indent}/* Semantic roles — dark ("dusk"), from config/design-tokens.json. */\n` +
  `${semanticBlock(semanticDark, indent)}\n` +
  `${indent}/* Today's --color-* names follow their roles at dusk. */\n` +
  `${legacyDarkBlock(indent)}`;

export const createCssVariables = (
  theme: Theme,
  flags: ThemeCssFlags = { darkEnabled: THEME_DARK_ENABLED, osFollow: THEME_OS_FOLLOW },
): string => {
  const darkBlock = flags.darkEnabled
    ? `\n    :root[data-theme="dark"] {\n${darkDeclarations('      ')}\n    }\n`
    : '';
  const osFollowBlock = flags.darkEnabled && flags.osFollow
    ? `\n    @media (prefers-color-scheme: dark) {\n      :root:not([data-theme="light"]) {\n${darkDeclarations('        ')}\n      }\n    }\n`
    : '';
  return `
    :root {
      color-scheme: light;

      /* Brand constants — never invert with a colour scheme.
         Use these for "this specific brand colour"; use the semantic
         --color-* tokens below for "the role this colour plays". */
      --brand-black: ${brandColors.primaryBlack};
      --brand-charcoal: ${brandColors.secondaryDark};
      --brand-gold: ${brandColors.accentGold};
      --brand-gold-alt: ${brandColors.accentGoldAlt};
      --brand-gold-hover: ${brandColors.hoverGold};
      --brand-gold-text: ${brandColors.accentText};
      --brand-canvas: ${brandColors.backgroundSoftWhite};
      --brand-surface: ${brandColors.surfaceSoft};
      --brand-text: ${brandColors.textPrimary};
      --brand-text-secondary: ${brandColors.textSecondary};
      --brand-text-muted: ${brandColors.textMuted};
      --brand-text-placeholder: ${brandColors.textPlaceholder};
      --brand-line: ${brandColors.border};
      --brand-line-strong: ${brandColors.borderStrong};
      --brand-danger: ${brandColors.dangerRed};

      /* Semantic roles — light ("noon"), from config/design-tokens.json. */
${semanticBlock(semanticLight, '      ')}

      /* Today's --color-* names, kept: an alias where the value already agrees
         with its role, the literal where it does not (PR-5 converges them). */
${legacyLightBlock(theme, '      ')}

      /* Spacing */
      --spacing-xs: ${theme.spacing.xs};
      --spacing-sm: ${theme.spacing.sm};
      --spacing-md: ${theme.spacing.md};
      --spacing-lg: ${theme.spacing.lg};
      --spacing-xl: ${theme.spacing.xl};
      --spacing-xxl: ${theme.spacing.xxl};

      /* Font Sizes */
      --font-size-tiny: ${theme.fontSizes.tiny};
      --font-size-small: ${theme.fontSizes.small};
      --font-size-body: ${theme.fontSizes.body};
      --font-size-subtitle: ${theme.fontSizes.subtitle};
      --font-size-title: ${theme.fontSizes.title};
      --font-size-heading: ${theme.fontSizes.heading};
      --font-size-display: ${theme.fontSizes.display};

      /* Radii */
      --radius-xs: ${theme.radii.xs};
      --radius-sm: ${theme.radii.sm};
      --radius-md: ${theme.radii.md};
      --radius-lg: ${theme.radii.lg};
      --radius-xl: ${theme.radii.xl};
      --radius-round: ${theme.radii.round};
      --radius-pill: ${theme.radii.pill};

      /* Shadows */
      --shadow-sm: ${theme.shadows.sm};
      --shadow-md: ${theme.shadows.md};
      --shadow-lg: ${theme.shadows.lg};
      --shadow-xl: ${theme.shadows.xl};
      --shadow-focus: ${theme.shadows.focus};

      /* Transitions */
      --transition-fast: ${theme.transitions.fast};
      --transition-default: ${theme.transitions.default};
      --transition-slow: ${theme.transitions.slow};

      /* Fonts */
      --font-family-display: ${theme.fonts.display};
      --font-family-heading: ${theme.fonts.heading};
      --font-family-text: ${theme.fonts.text};
      --font-family-mono: ${theme.fonts.mono};
    }
${darkBlock}${osFollowBlock}  `;
};
